import {
  toWSMoneyString,
  useIsMobile,
  WSButton,
  WSButtons,
  WSCircle,
  WSDateRangeSelect,
  WSDateRangeSelectOptions,
  WSDateRangeSelectRangeName,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSPage,
  WSPanel,
  WSPreview,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useState } from "react";
import { QueryStatus } from "react-query";
import { Route, useHistory } from "react-router-dom";
import image1 from "../../assets/images/invoices-feature-1.png";
import image2 from "../../assets/images/invoices-feature-2.png";
import image3 from "../../assets/images/invoices-feature-3.png";
import image4 from "../../assets/images/invoices-feature-4.png";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import { ProductActivationWrapper } from "../../components/ProductActivationWrapper/ProductActivationWrapper";
import { useUserId } from "../../query/hooks/helpers";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useInvoicesQuery,
  useInvoiceTemplatesQuery
} from "../../query/payments/queries";
import {
  getInvoices,
  getInvoiceTemplates
} from "../../query/payments/selectors";
import { useActivities } from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import { useSetWSStore, useWSStore } from "../../store";
import { InvoicesTable } from "../components/InvoicesTable/InvoicesTable";
import { PaymentsStatusBanner } from "../components/PaymentsStatusBanner/PaymentsStatusBanner";
import { RecentClients } from "../components/RecentClients/RecentClients";
import { useVerificationUpgrade } from "../components/VerificationUpgrade/VerificationUpgrade";
import { calculateInvoicesTotal } from "../utils";
import { InvoicesInvoiceDetails } from "./InvoicesInvoiceDetails";
import { InvoicesInvoiceTemplateDetails } from "./InvoicesInvoiceTemplateDetails";

export const InvoicesDashboard = () => {
  const history = useHistory();
  const invoicesQuery = useInvoicesQuery();
  const invoiceTemplatesQuery = useInvoiceTemplatesQuery();
  const isMobile = useIsMobile();
  const store = useWSStore();
  const setStore = useSetWSStore();
  const userId = useUserId();
  const activityQuery = useActivities(userId);
  const {
    requestVerification,
    requestVerificationMeta,
    isBankingRequested,
    isCardPaymentsRequested
  } = useVerificationUpgrade();
  const featureFlags = useFeatureFlags();

  const [statsRange, setStatsRange] = useState(
    WSDateRangeSelectOptions.AllTime.range
  );
  const [statsRangeLabel, setStatsRangeLabel] = useState(
    WSDateRangeSelectOptions.AllTime.label
  );

  const createInvoiceHandler = featureFlags.data?.verificationsByFeature
    ? async () => {
        try {
          if (!isBankingRequested || !isCardPaymentsRequested) {
            await requestVerification({
              banking: !isBankingRequested,
              cardPayments: !isCardPaymentsRequested
            });
          }
          history.push("/member/invoices/create", {
            backPath: "/member/invoices"
          });
        } catch (e) {
          console.error(e);
        }
      }
    : () => {
        history.push("/member/invoices/create", {
          backPath: "/member/invoices"
        });
      };

  const enablePaymentsButtonHandler = () => {
    history.push("/member/invoices/set-up");
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const stuckQuery = [invoicesQuery, invoiceTemplatesQuery].find(
        query => query.status === QueryStatus.Loading
      );

      if (stuckQuery) {
        stuckQuery.refetch();
      }
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [invoicesQuery, invoiceTemplatesQuery]);

  return (
    <>
      <Route
        path="/member/invoices/template/:invoiceTemplateId"
        component={InvoicesInvoiceTemplateDetails}
      />

      <Route
        path="/member/invoices/:invoiceId"
        component={InvoicesInvoiceDetails}
        exact
      />

      <BrowserPageTitle title="Invoices" />

      <ProductActivationWrapper>
        <WSQueries
          queries={{
            activityQuery
          }}
          isPage
        >
          {({ activityQuery: { data: activity } }) =>
            activity.flows.paymentsSetup?.complete ? (
              <WSQueries
                queries={{
                  invoicesQuery,
                  invoiceTemplatesQuery
                }}
              >
                {({ invoicesQuery, invoiceTemplatesQuery }) => {
                  const invoices = getInvoices(invoicesQuery.data);
                  const invoiceTemplates = getInvoiceTemplates(
                    invoiceTemplatesQuery.data
                  ).filter(
                    invoiceTemplate =>
                      !invoiceTemplate.labels?.parentInvoiceTemplateId
                  );

                  const outstandingInvoices = invoices.filter(
                    invoice =>
                      invoice.status === InvoiceStatus.Open ||
                      invoice.status === InvoiceStatus.Overdue
                  );

                  const overdueInvoices = invoices.filter(
                    invoice =>
                      invoice.status === InvoiceStatus.Overdue &&
                      (statsRange && invoice.events.openedAt
                        ? statsRange[0] < invoice.events.openedAt &&
                          invoice.events.openedAt < statsRange[1]
                        : true)
                  );

                  const paidInvoices = invoices.filter(
                    invoice =>
                      (invoice.status === InvoiceStatus.Paid ||
                        invoice.status === InvoiceStatus.PaymentInTransit) &&
                      (statsRange && invoice.events.paidAt
                        ? statsRange[0] < invoice.events.paidAt &&
                          invoice.events.paidAt < statsRange[1]
                        : true)
                  );

                  const pendingInvoices = invoices.filter(
                    invoice =>
                      ((invoice.status === InvoiceStatus.Paid &&
                        !!invoice.amountDetails &&
                        !invoice.events.depositedAt) ||
                        invoice.status === InvoiceStatus.PaymentInTransit) &&
                      (statsRange && invoice.events.paidAt
                        ? statsRange[0] < invoice.events.paidAt &&
                          invoice.events.paidAt < statsRange[1]
                        : true)
                  );

                  const allUnsentInvoices = invoices.filter(
                    invoice =>
                      (invoice.status === InvoiceStatus.Draft ||
                        invoice.status === InvoiceStatus.Pending) &&
                      invoice.labels?.createdBy === userId
                  );

                  const allOutstandingInvoices = invoices.filter(
                    invoice =>
                      invoice.status === InvoiceStatus.Open ||
                      invoice.status === InvoiceStatus.Overdue
                  );

                  const allPaidInvoices = invoices.filter(
                    invoice =>
                      invoice.status === InvoiceStatus.Paid ||
                      invoice.status === InvoiceStatus.PaymentInTransit
                  );

                  return (
                    <WSPage
                      title="Invoicing"
                      primaryButtonComponent={props => (
                        <WSButton
                          name="createInvoice"
                          onClick={createInvoiceHandler}
                          {...props}
                        >
                          Create invoice
                        </WSButton>
                      )}
                    >
                      {requestVerificationMeta.isLoading && <OverlaySpinner />}
                      <PaymentsStatusBanner mb="M" />
                      <WSFlexBox justify="space-between" wrap="nowrap">
                        <WSDateRangeSelect
                          mb="M"
                          onChange={(value, label) => {
                            setStatsRange(value);
                            setStatsRangeLabel(label);
                          }}
                          label={statsRangeLabel}
                          value={statsRange}
                          options={[
                            WSDateRangeSelectRangeName.AllTime,
                            WSDateRangeSelectRangeName.PastWeek,
                            WSDateRangeSelectRangeName.PastMonth,
                            WSDateRangeSelectRangeName.Past12Months,
                            WSDateRangeSelectRangeName.Custom
                          ]}
                        />
                        <WSButton.Link
                          onClick={() => {
                            history.push("/member/invoices/list");
                          }}
                        >
                          All invoices
                        </WSButton.Link>
                      </WSFlexBox>

                      <WSGrid.Container>
                        <WSGrid.Item S={6}>
                          <WSPanel>
                            <WSFlexBox.CenterY
                              justify="space-between"
                              wrap="nowrap"
                            >
                              <WSCircle.Icon
                                size="L"
                                icon="envelopeCheckmark"
                                color="blue100"
                                secondaryColor="blue500"
                                mr="M"
                              />
                              <WSElement style={{ flex: 1 }}>
                                <WSText.Heading3 formatMoney mb="XS">
                                  {calculateInvoicesTotal(outstandingInvoices)}
                                </WSText.Heading3>
                                <WSText color="grey700">
                                  outstanding
                                  {overdueInvoices.length > 0 && (
                                    <>
                                      {" "}
                                      <WSText color="red500" inline>
                                        (
                                        {toWSMoneyString(
                                          calculateInvoicesTotal(
                                            overdueInvoices
                                          )
                                        )}{" "}
                                        overdue)
                                      </WSText>
                                    </>
                                  )}
                                </WSText>
                              </WSElement>
                            </WSFlexBox.CenterY>
                          </WSPanel>
                        </WSGrid.Item>

                        <WSGrid.Item S={6}>
                          <WSPanel
                            onClick={
                              allPaidInvoices.length > 0
                                ? () => {
                                    history.push("/member/invoices/paid");
                                  }
                                : undefined
                            }
                          >
                            <WSFlexBox.CenterY
                              justify="space-between"
                              wrap="nowrap"
                            >
                              <WSCircle.Icon
                                size="L"
                                icon="doubleSuccessMark"
                                color="green100"
                                secondaryColor="green500"
                                mr="M"
                              />
                              <WSElement style={{ flex: 1 }}>
                                <WSText.Heading3 color="green500" mb="XS">
                                  +
                                  {toWSMoneyString(
                                    calculateInvoicesTotal(paidInvoices)
                                  )}
                                </WSText.Heading3>
                                <WSText color="grey700">
                                  paid
                                  {pendingInvoices.length > 0 && (
                                    <>
                                      {" "}
                                      <WSText inline>
                                        (
                                        {toWSMoneyString(
                                          calculateInvoicesTotal(
                                            pendingInvoices
                                          )
                                        )}{" "}
                                        pending)
                                      </WSText>
                                    </>
                                  )}
                                </WSText>
                              </WSElement>
                              {allPaidInvoices.length > 0 && (
                                <WSIcon
                                  name="chevronRight"
                                  color="blue"
                                  size="S"
                                />
                              )}
                            </WSFlexBox.CenterY>
                          </WSPanel>
                        </WSGrid.Item>
                      </WSGrid.Container>

                      <RecentClients my="XL" />

                      <InvoicesTable
                        title="Unsent"
                        invoices={allUnsentInvoices}
                        invoiceTemplates={invoiceTemplates}
                        emptyState={
                          <>
                            <WSText.Heading3 mb="M">Unsent</WSText.Heading3>
                            <WSDivider mb="M" />
                            <WSFlexBox
                              direction="column"
                              alignItems="center"
                              wrap="nowrap"
                            >
                              <WSFlexBox mb="S">
                                <WSCircle.Icon
                                  icon="pencil"
                                  color="grey200"
                                  secondaryColor="grey600"
                                  mr="S"
                                />
                                <WSCircle.Icon
                                  icon="calendar"
                                  color="grey200"
                                  secondaryColor="grey600"
                                />
                              </WSFlexBox>
                              <WSText color="grey600">
                                Drafts, scheduled, and future recurring invoices
                                will be listed here
                              </WSText>
                            </WSFlexBox>
                          </>
                        }
                        mb="XL"
                      />
                      <InvoicesTable
                        title="Outstanding"
                        invoices={allOutstandingInvoices}
                        emptyState={
                          <>
                            <WSText.Heading3 mb="M">
                              Outstanding
                            </WSText.Heading3>
                            <WSDivider mb="M" />
                            <WSFlexBox
                              direction="column"
                              alignItems="center"
                              wrap="nowrap"
                            >
                              <WSFlexBox mb="S">
                                <WSCircle.Icon
                                  icon="envelopeCheckmark"
                                  color="blue100"
                                  secondaryColor="blue500"
                                  mr="S"
                                />
                                <WSCircle.Icon
                                  icon="envelopeCheckmark"
                                  color="blue100"
                                  secondaryColor="blue500"
                                  badge={{
                                    text: "!",
                                    color: "red500",
                                    secondaryColor: "white"
                                  }}
                                />
                              </WSFlexBox>
                              <WSText color="grey600">
                                Open invoices will be listed here until they are
                                paid
                              </WSText>
                            </WSFlexBox>
                          </>
                        }
                      />
                    </WSPage>
                  );
                }}
              </WSQueries>
            ) : (
              <WSPage>
                <WSGrid.Container>
                  <WSGrid.Item L={5}>
                    <WSText.Heading2 mb="S">Get paid your way.</WSText.Heading2>
                    <WSText mb={isMobile ? "M" : "XL"} color="grey700">
                      Send invoices effortlessly with tools that make it easier
                      for clients to pay up (and harder for them not to).
                    </WSText>

                    <WSButtons>
                      <WSButton
                        onClick={enablePaymentsButtonHandler}
                        name="enablePayments"
                      >
                        Set up Payments
                      </WSButton>
                      {store.productActivationAfterOnboarding && (
                        <WSButton.Secondary
                          onClick={() => {
                            history.push("/member/dashboard");
                            setStore({
                              productActivationAfterOnboarding: false
                            });
                          }}
                        >
                          Explore Wingspan
                        </WSButton.Secondary>
                      )}
                    </WSButtons>
                  </WSGrid.Item>
                </WSGrid.Container>

                <WSPreview
                  mt={isMobile ? "L" : "XXL"}
                  list={[
                    {
                      title: "Personal payment links",
                      description:
                        "Share your personal pay link. Get paid. It’s actually that simple.",
                      image: image1,
                      imageWidth: 392
                    },
                    {
                      title: "Supercharged custom invoices",
                      description:
                        "Set up clients on retainer, add late fees, and manage who pays for processing fees.",
                      image: image2,
                      imageWidth: 315
                    },
                    {
                      title: "Late invoice automation",
                      description:
                        "Never send another “following up” email again — Wingspan chases overdue invoices on your behalf.",
                      image: image3,
                      imageWidth: 373
                    },
                    {
                      title: "Payment notifications",
                      description:
                        "Know exactly when payments hit your bank account, not just when clients say they’re sent.",
                      image: image4,
                      imageWidth: 396
                    }
                  ]}
                />
              </WSPage>
            )
          }
        </WSQueries>
      </ProductActivationWrapper>
    </>
  );
};
